import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { User, Lock, Mail, Phone } from 'lucide-react';
import { PUBLIC_BASE } from './config';
import PublicLayout from '../../components/public/PublicLayout';

const EMAIL_DOMAINS = ['gmail.com', 'naver.com', 'daum.net', 'hanmail.net', 'nate.com', 'kakao.com', 'custom'];

function MemberRegisterPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    loginId: '',
    password: '',
    passwordConfirm: '',
    name: '',
    nickName: '',
    emailId: '',
    emailDomain: 'gmail.com',
    customDomain: '',
    phone1: '',
    phone2: '',
    phone3: '',
  });

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.loginId || !form.password || !form.name) {
      return alert('아이디, 비밀번호, 이름은 필수 입력값입니다.');
    }
    if (form.password !== form.passwordConfirm) {
      return alert('비밀번호가 일치하지 않습니다.');
    }
    if (form.password.length < 4) {
      return alert('비밀번호는 4자리 이상이어야 합니다.');
    }

    const emailDomain = form.emailDomain === 'custom' ? form.customDomain : form.emailDomain;

    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/auth/member/register`, {
        loginId: form.loginId,
        password: form.password,
        name: form.name,
        nickName: form.nickName,
        emailId: form.emailId,
        emailDomain,
        phone1: form.phone1,
        phone2: form.phone2,
        phone3: form.phone3,
      });

      if (res.data.token) {
        localStorage.setItem('token', res.data.token);
        alert('회원가입이 완료되었습니다.');
        navigate(PUBLIC_BASE || '/');
      }
    } catch (err) {
      alert(err.response?.data?.message || '회원가입에 실패했습니다.');
    }
  };

  const inputClass =
    'w-full pl-9 pr-4 py-2.5 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-sm text-slate-200 placeholder-slate-500 outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-transparent transition';
  const inputClassNoPad =
    'w-full px-3 py-2.5 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-sm text-slate-200 placeholder-slate-500 outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-transparent transition';

  return (
    <PublicLayout>
    <div className="flex justify-center items-center py-16 px-4 min-h-[60vh]">
      <div className="w-full max-w-md">
        <div className="bg-[#0d1526] rounded-t-2xl px-8 pt-8 pb-6 border border-white/[0.08] border-b-0">
          <h1 className="text-white text-2xl font-bold tracking-tight">회원가입</h1>
          <p className="text-slate-400 text-sm mt-1">p!nk 회원이 되어주세요</p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-[#182334] rounded-b-2xl px-8 py-8 space-y-5 border border-white/[0.08] border-t-0">
          {/* 아이디 */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
              아이디 <span className="text-rose-400">*</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <User size={15} className="text-slate-500" />
              </div>
              <input
                type="text"
                className={inputClass}
                placeholder="영문/숫자 조합"
                value={form.loginId}
                onChange={handleChange('loginId')}
                autoComplete="username"
              />
            </div>
          </div>

          {/* 비밀번호 */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
              비밀번호 <span className="text-rose-400">*</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <Lock size={15} className="text-slate-500" />
              </div>
              <input
                type="password"
                className={inputClass}
                placeholder="4자리 이상"
                value={form.password}
                onChange={handleChange('password')}
                autoComplete="new-password"
              />
            </div>
          </div>

          {/* 비밀번호 확인 */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
              비밀번호 확인 <span className="text-rose-400">*</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <Lock size={15} className="text-slate-500" />
              </div>
              <input
                type="password"
                className={inputClass}
                placeholder="비밀번호 재입력"
                value={form.passwordConfirm}
                onChange={handleChange('passwordConfirm')}
                autoComplete="new-password"
              />
            </div>
          </div>

          {/* 이름 */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
              이름 <span className="text-rose-400">*</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <User size={15} className="text-slate-500" />
              </div>
              <input
                type="text"
                className={inputClass}
                placeholder="이름"
                value={form.name}
                onChange={handleChange('name')}
              />
            </div>
          </div>

          {/* 닉네임 */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
              닉네임
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <User size={15} className="text-slate-500" />
              </div>
              <input
                type="text"
                className={inputClass}
                placeholder="닉네임"
                value={form.nickName}
                onChange={handleChange('nickName')}
              />
            </div>
          </div>

          {/* 이메일 */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
              이메일
            </label>
            <div className="flex items-center gap-1.5">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                  <Mail size={15} className="text-slate-500" />
                </div>
                <input
                  type="text"
                  className={inputClass}
                  placeholder="이메일"
                  value={form.emailId}
                  onChange={handleChange('emailId')}
                />
              </div>
              <span className="text-slate-500 text-sm">@</span>
              {form.emailDomain === 'custom' ? (
                <input
                  type="text"
                  className={`${inputClassNoPad} flex-1`}
                  placeholder="직접입력"
                  value={form.customDomain}
                  onChange={handleChange('customDomain')}
                />
              ) : (
                <select
                  className={`${inputClassNoPad} flex-1`}
                  value={form.emailDomain}
                  onChange={handleChange('emailDomain')}>
                  {EMAIL_DOMAINS.map((d) => (
                    <option key={d} value={d}>
                      {d === 'custom' ? '직접입력' : d}
                    </option>
                  ))}
                </select>
              )}
            </div>
            {form.emailDomain === 'custom' && (
              <button
                type="button"
                onClick={() => setForm((p) => ({ ...p, emailDomain: 'gmail.com', customDomain: '' }))}
                className="text-xs text-sky-400 hover:underline mt-1">
                목록에서 선택
              </button>
            )}
          </div>

          {/* 전화번호 */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
              전화번호
            </label>
            <div className="flex items-center gap-1.5">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                  <Phone size={15} className="text-slate-500" />
                </div>
                <input
                  type="text"
                  className={inputClass}
                  placeholder="010"
                  value={form.phone1}
                  onChange={handleChange('phone1')}
                  maxLength={3}
                />
              </div>
              <span className="text-slate-500">-</span>
              <input
                type="text"
                className={inputClassNoPad + ' flex-1'}
                placeholder="0000"
                value={form.phone2}
                onChange={handleChange('phone2')}
                maxLength={4}
              />
              <span className="text-slate-500">-</span>
              <input
                type="text"
                className={inputClassNoPad + ' flex-1'}
                placeholder="0000"
                value={form.phone3}
                onChange={handleChange('phone3')}
                maxLength={4}
              />
            </div>
          </div>

          {/* 가입 버튼 */}
          <button
            type="submit"
            className="w-full flex items-center justify-center gap-2 px-4 py-3 mt-2 bg-sky-500 hover:bg-sky-600 text-white text-sm font-semibold rounded-xl transition-colors duration-200 cursor-pointer">
            회원가입
          </button>

          <p className="text-center text-sm text-slate-400 mt-2">
            이미 회원이신가요?{' '}
            <Link to={`${PUBLIC_BASE}/login`} className="text-sky-400 hover:text-sky-300 hover:underline">
              로그인
            </Link>
          </p>
        </form>
      </div>
    </div>
    </PublicLayout>
  );
}

export default MemberRegisterPage;
